import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {Spinner} from 'react-bootstrap';
import styled, {css} from 'styled-components';
import {Icon} from 'shared/helpers';
import {mobile, size} from 'shared/Breakpoints';
import {useCustomMediaQuery} from 'shared/helpers/DeviceSize';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {useTopBarControls} from 'components/customer/RoomPlanner/helper/useTopBarControls';
import UndoRedoComponent from 'components/customer/RoomPlanner/components/UndoRedoComponent';
import {frontsIconName} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {
    getTopBarPresentation,
    iconForViewOption,
} from 'components/customer/RoomPlanner/helpers/plannerView';

/**
 * A top bar dropdown that its button opens and closes, and that closes when
 * a click lands outside it.
 * @param {string} attribute The data attribute marking the dropdown's
 * wrapper. By attribute, not a ref: the right-hand controls render twice
 * (one hidden copy measures widths), so a ref can land on the copy.
 * @return {object} Whether it is open, and a toggle for its button.
 */
const useDropdown = (attribute: string) => {
    const [open, setOpen] = useState(false);
    const toggle = useCallback(() => setOpen((isOpen) => !isOpen), []);
    useEffect(() => {
        if (!open) return undefined;
        const closeOnOutsideClick = (event: PointerEvent) => {
            const target = event.target as Element | null;
            if (!target?.closest?.(`[${attribute}]`)) setOpen(false);
        };
        document.addEventListener('pointerdown', closeOnOutsideClick);

        return () =>
            document.removeEventListener('pointerdown', closeOnOutsideClick);
    }, [open, attribute]);

    return {open, toggle};
};

const TopBar = () => {
    const barRef = useRef<HTMLElement>(null);
    const leftRef = useRef<HTMLDivElement>(null);
    const centerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const rightCollapsedRef = useRef(false);
    const [rightCollapsed, setRightCollapsed] = useState(false);
    const [doorMenuOpen, setDoorMenuOpen] = useState(false);
    // Same breakpoint as the `mobile` css helper.
    const isPhone = useCustomMediaQuery({maxWidth: size.sm - 1});
    // Phone and tablet: undo/redo moves out of the bar onto the chip row.
    const isCompact = useCustomMediaQuery({maxWidth: size.md});
    const [stripBox, setStripBox] = useState<{
        top: number;
        left: number;
        right: number;
    } | null>(null);
    const isRoomPreview = useRoomPreview();
    const {
        mode,
        activePreset,
        doorFrontsMode,
        doorFrontsPose,
        doorFrontsAnimating,
        showArrows,
        isWireframeMode,
        snapSettings,
        viewsLocked,
        select3D,
        selectPlan,
        selectElevation,
        resetView,
        selectOption,
        toggleWireframe,
        selectDoorFront,
        toggleSnapOption,
        toggleDimensions,
        printReport,
    } = useTopBarControls();
    // The Positioning Tools dropdown (CO-5856).
    const toolsMenu = useDropdown('data-positioning-tools');
    const toggleDoorMenu = useCallback(() => {
        setDoorMenuOpen((open) => !open);
    }, []);

    useEffect(() => {
        if (doorFrontsAnimating) return;
        setDoorMenuOpen(false);
    }, [doorFrontsMode, doorFrontsAnimating]);

    const syncRightLayout = useCallback(() => {
        const bar = barRef.current;
        const left = leftRef.current;
        const center = centerRef.current;
        const measure = measureRef.current;
        if (!bar || !left || !measure || measure.offsetWidth === 0) return;
        if (bar.clientWidth === 0) return;
        const styles = getComputedStyle(bar);
        const padLeft = parseFloat(styles.paddingLeft) || 0;
        const padRight = parseFloat(styles.paddingRight) || 0;
        const parsedGap = parseFloat(styles.columnGap);
        const gap = Number.isFinite(parsedGap) ? parsedGap : 12;
        const available =
            bar.clientWidth -
            padLeft -
            padRight -
            left.offsetWidth -
            (center?.offsetWidth ?? 0) -
            gap * 2;
        const next = measure.offsetWidth > available - 4;
        if (rightCollapsedRef.current === next) return;
        rightCollapsedRef.current = next;
        setRightCollapsed(next);
    }, []);

    const syncStrip = useCallback(() => {
        const bar = barRef.current;

        if (!bar) return;

        const rect = bar.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const top = Math.round(rect.bottom + 8);
        const left = Math.round(rect.left + 14);
        // Keeps the mobile tool row clear of the corner beta ribbon.
        const right = Math.round(
            window.innerWidth - rect.right + BETA_RIBBON_CLEARANCE
        );

        setStripBox((current) => {
            if (
                current &&
                current.top === top &&
                current.left === left &&
                current.right === right
            ) {
                return current;
            }

            return {top, left, right};
        });
    }, []);

    useLayoutEffect(() => {
        syncRightLayout();
        syncStrip();
        const bar = barRef.current;
        const left = leftRef.current;
        const center = centerRef.current;
        const measure = measureRef.current;
        if (!bar) return undefined;

        let animationFrame = 0;
        const schedule = () => {
            if (animationFrame) return;
            animationFrame = requestAnimationFrame(() => {
                animationFrame = 0;
                syncRightLayout();
                syncStrip();
            });
        };
        const observer = new ResizeObserver(schedule);
        observer.observe(bar);
        if (left) observer.observe(left);
        if (center) observer.observe(center);
        if (measure) observer.observe(measure);
        window.addEventListener('scroll', schedule, true);
        window.addEventListener('resize', schedule);
        return () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            observer.disconnect();
            window.removeEventListener('scroll', schedule, true);
            window.removeEventListener('resize', schedule);
        };
    }, [syncRightLayout, syncStrip, mode, isWireframeMode, isRoomPreview]);

    const frontsIcon = frontsIconName(doorFrontsMode, doorFrontsPose);
    const openFrontsBusy = doorFrontsAnimating && doorFrontsMode === 'open';
    const shutFrontsBusy = doorFrontsAnimating && doorFrontsMode === 'shut';
    const renderFrontChoices = () => (
        <>
            <ViewChoice
                type="button"
                title="Open fronts"
                data-mode="open"
                $active={doorFrontsMode === 'open'}
                aria-pressed={doorFrontsMode === 'open'}
                aria-busy={openFrontsBusy}
                disabled={doorFrontsAnimating}
                onClick={selectDoorFront}>
                {openFrontsBusy ? (
                    <FrontsSpinner animation="border" size="sm" />
                ) : null}
                Open
            </ViewChoice>
            <ViewChoice
                type="button"
                title="Shut fronts"
                data-mode="shut"
                $active={doorFrontsMode === 'shut'}
                aria-pressed={doorFrontsMode === 'shut'}
                aria-busy={shutFrontsBusy}
                disabled={doorFrontsAnimating}
                onClick={selectDoorFront}>
                {shutFrontsBusy ? (
                    <FrontsSpinner animation="border" size="sm" />
                ) : null}
                Shut
            </ViewChoice>
            <ViewChoice
                type="button"
                title="Hide fronts"
                data-mode="hide"
                $active={doorFrontsMode === 'hide'}
                aria-pressed={doorFrontsMode === 'hide'}
                disabled={doorFrontsAnimating}
                onClick={selectDoorFront}>
                Hide
            </ViewChoice>
        </>
    );
    const presentation = getTopBarPresentation({
        mode,
        doorFrontsMode,
        showArrows,
        isWireframeMode,
        showPrint: !isRoomPreview,
    });
    // A switch for one snapping choice in the Positioning Tools dropdown.
    const renderSwitch = (
        name: 'snapping' | 'align' | 'guides',
        label: string,
        icon: string,
        on: boolean,
        title: string
    ) => (
        <ToolRow data-cy={`${name}-row`}>
            <ToolName>
                <ToolIcon iconName={icon} />
                {label}
            </ToolName>
            <Switch
                type="button"
                role="switch"
                data-cy={`${name}-switch`}
                data-option={name}
                title={title}
                aria-label={label}
                aria-checked={on}
                $on={on}
                onClick={toggleSnapOption}
            />
        </ToolRow>
    );
    // Positioning Tools: a dropdown with a switch each for Snapping (every
    // kind of snap), Align and Guides (CO-5856). Not in the room preview,
    // where nothing is dragged.
    const renderPositioningTools = () => (
        <ToolsMenu data-positioning-tools>
            <LabelledButton
                type="button"
                data-cy="positioning-tools-button"
                title="Positioning Tools"
                $active={toolsMenu.open}
                aria-expanded={toolsMenu.open}
                onClick={toolsMenu.toggle}>
                <ControlIcon iconName="Positioning.svg" />
                <IconOnlyLabel>Positioning Tools</IconOnlyLabel>
                <Chevron iconName="Chevron-down.svg" $open={toolsMenu.open} />
            </LabelledButton>
            {toolsMenu.open ? (
                <ToolsPanel
                    data-cy="positioning-tools"
                    $alignRight={rightCollapsed || isPhone}>
                    {renderSwitch(
                        'snapping',
                        'Snapping',
                        'Snapping.svg',
                        snapSettings.snapping,
                        snapSettings.snapping
                            ? 'Turn snapping off (S)'
                            : 'Turn snapping on (S)'
                    )}
                    {renderSwitch(
                        'align',
                        'Align',
                        'Align.svg',
                        snapSettings.align,
                        snapSettings.align
                            ? 'Turn alignment off (A)'
                            : 'Turn alignment on (A)'
                    )}
                    {renderSwitch(
                        'guides',
                        'Guides',
                        'Guides.svg',
                        snapSettings.guides,
                        snapSettings.guides
                            ? 'Hide movement guides (G)'
                            : 'Show movement guides (G)'
                    )}
                </ToolsPanel>
            ) : null}
        </ToolsMenu>
    );

    const renderRightControls = () => (
        <>
            <LabelledButton
                type="button"
                title={presentation.wireframeLabel}
                onClick={toggleWireframe}>
                <ControlIcon
                    color="secondary"
                    iconName={
                        isWireframeMode
                            ? 'preview/Wireframe.svg'
                            : 'preview/Solid.svg'
                    }
                />
                <IconOnlyLabel>{presentation.wireframeLabel}</IconOnlyLabel>
            </LabelledButton>
            <PhoneDoor>
                <LabelledButton
                    type="button"
                    title="Fronts"
                    $active={doorFrontsMode !== 'shut' || doorMenuOpen}
                    aria-pressed={doorFrontsMode !== 'shut'}
                    onClick={toggleDoorMenu}>
                    <ControlIcon color="secondary" iconName={frontsIcon} />
                </LabelledButton>
                {doorMenuOpen ? (
                    <DoorPopover $alignHide={isRoomPreview}>
                        <ViewSwitch $locked={false}>
                            {renderFrontChoices()}
                        </ViewSwitch>
                    </DoorPopover>
                ) : null}
            </PhoneDoor>
            <DesktopFronts>
                <ViewSwitch $locked={false}>
                    <ControlIcon color="secondary" iconName={frontsIcon} />
                    <DoorFrontsLabel>Fronts</DoorFrontsLabel>
                    {renderFrontChoices()}
                </ViewSwitch>
            </DesktopFronts>
            {isRoomPreview ? null : renderPositioningTools()}
            <LabelledButton
                type="button"
                title="Dimensions"
                $active={presentation.dimensionsHighlighted}
                aria-pressed={presentation.dimensionsHighlighted}
                onClick={toggleDimensions}>
                <ControlIcon iconName="Measurements.svg" />
                <IconOnlyLabel>Dimensions</IconOnlyLabel>
            </LabelledButton>
            {presentation.right.includes('Print') ? (
                <LabelledButton
                    type="button"
                    title="Print"
                    onClick={printReport}>
                    <ControlIcon iconName="PDF.svg" />
                    <IconOnlyLabel>Print</IconOnlyLabel>
                </LabelledButton>
            ) : null}
        </>
    );

    const viewChips = presentation.options.map((option) => (
        <OptionChip
            key={option.id}
            type="button"
            data-preset={option.id}
            $active={activePreset === option.id}
            onClick={selectOption}
            disabled={viewsLocked}>
            <ChipIcon
                color="secondary"
                iconName={iconForViewOption(option.id)}
            />
            {option.label}
        </OptionChip>
    ));
    const optionStrip =
        !isCompact && stripBox && viewChips.length ? (
            <OptionStrip style={{top: stripBox.top, left: stripBox.left}}>
                {viewChips}
            </OptionStrip>
        ) : null;
    const compactTools =
        isCompact && stripBox ? (
            <CompactToolRow
                style={{
                    top: stripBox.top,
                    left: stripBox.left,
                    right: stripBox.right,
                }}>
                {viewChips.length ? <ChipGroup>{viewChips}</ChipGroup> : null}
                <CompactUndo $centered={!isPhone}>
                    <UndoRedoComponent />
                </CompactUndo>
            </CompactToolRow>
        ) : null;

    return (
        <Bar ref={barRef}>
            <LeftCluster ref={leftRef}>
                <ViewSwitch $locked={viewsLocked}>
                    <ViewButtonWrap>
                        <ViewChoice
                            type="button"
                            title="3D view"
                            $active={mode === '3D'}
                            onClick={select3D}
                            disabled={viewsLocked}>
                            <ViewFull>3D view</ViewFull>
                            <ViewShort>3D</ViewShort>
                        </ViewChoice>
                    </ViewButtonWrap>
                    <ViewButtonWrap>
                        <ViewChoice
                            type="button"
                            title="Plan"
                            $active={mode === 'PLAN'}
                            onClick={selectPlan}
                            disabled={viewsLocked}>
                            Plan
                        </ViewChoice>
                    </ViewButtonWrap>
                    <ViewButtonWrap>
                        <ViewChoice
                            type="button"
                            title="Elevation"
                            $active={mode === 'ELEVATION'}
                            onClick={selectElevation}
                            disabled={viewsLocked}>
                            <ViewFull>Elevation</ViewFull>
                            <ViewShort>Elev</ViewShort>
                        </ViewChoice>
                    </ViewButtonWrap>
                </ViewSwitch>
                {presentation.resetViewVisible ? (
                    <ResetView
                        type="button"
                        title="Reset view"
                        onClick={resetView}
                        disabled={viewsLocked}>
                        <ControlIcon
                            color="secondary"
                            iconName="Re-centre.svg"
                        />
                        <CompactLabel>Reset view</CompactLabel>
                    </ResetView>
                ) : null}
            </LeftCluster>
            <CenterSlot ref={centerRef}>
                {isCompact ? null : <UndoRedoComponent />}
            </CenterSlot>
            <RightSlot>
                <RightCluster $icons={rightCollapsed}>
                    {renderRightControls()}
                </RightCluster>
            </RightSlot>
            <RightMeasure ref={measureRef} aria-hidden inert="true">
                <RightCluster>{renderRightControls()}</RightCluster>
            </RightMeasure>
            {optionStrip}
            {compactTools}
        </Bar>
    );
};

const narrowPlanner = '@container planner-stage (max-width: 1360px)';
// Rotated beta ribbon reaches about this far in from the scene's right edge.
const BETA_RIBBON_CLEARANCE = 56;

const Bar = styled.header`
    position: relative;
    z-index: 30;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    min-height: 48px;
    padding: 8px 14px;
    background: #fff;
    border-bottom: 1px solid #eceff3;
    flex-shrink: 0;
    overflow: visible;

    ${narrowPlanner} {
        gap: 8px;
        padding-top: 6px;
        padding-bottom: 6px;
    }

    ${mobile`
        gap: 4px;
        min-height: 40px;
        padding: 6px 8px;

        .planner-right-label {
            display: none;
        }
    `}
`;

const LeftCluster = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;

    ${mobile`
        gap: 4px;
    `}
`;

const CenterSlot = styled.div`
    justify-self: center;
    min-width: 0;
`;

const RightSlot = styled.div`
    position: relative;
    justify-self: end;
`;

const RightCluster = styled.div<{$icons?: boolean}>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    ${mobile`
        gap: 4px;
    `}

    ${({$icons}) =>
        $icons &&
        css`
            .planner-right-label {
                display: none;
            }
        `}
`;

const RightMeasure = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: max-content;
    visibility: hidden;
    pointer-events: none;

    .planner-right-label {
        display: inline !important;
    }
`;

const ViewSwitch = styled.div<{$locked: boolean}>`
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background: #f3f4f6;
    border-radius: 10px;
    opacity: ${({$locked}) => ($locked ? 0.55 : 1)};
`;

const ViewButtonWrap = styled.div`
    position: relative;
`;

const ViewChoice = styled.button<{$active?: boolean}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1.5px solid
        ${({$active, theme}) =>
            $active ? theme.colors.primary.main : 'transparent'};
    border-radius: 8px;
    padding: 5px 12px;
    background: ${({$active}) => ($active ? '#fff' : 'transparent')};
    color: ${({$active}) => ($active ? '#1f2937' : '#6b7280')};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: default;
    }

    ${mobile`
        padding: 4px 8px;
        font-size: 11px;
    `}
`;

const ViewFull = styled.span`
    ${mobile`
        display: none;
    `}
`;

const ViewShort = styled.span`
    display: none;

    ${mobile`
        display: inline;
    `}
`;

const CompactToolRow = styled.div`
    position: fixed;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const ChipGroup = styled.div`
    display: flex;
    flex-shrink: 0;
    gap: 6px;
`;

/* Phone: sits after the chips with a gap. Tablet: centred on the row. */
const CompactUndo = styled.div<{$centered?: boolean}>`
    flex-shrink: 0;

    ${({$centered}) =>
        $centered
            ? css`
                  position: absolute;
                  left: 50%;
                  transform: translateX(-50%);
              `
            : css`
                  margin-left: 12px;
              `}

    .undo-redo-label {
        display: none;
    }

    .undo-redo-action {
        padding: 4px;
    }
`;

const OptionStrip = styled.div`
    position: fixed;
    display: flex;
    gap: 6px;
`;

const OptionChip = styled.button<{$active?: boolean}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1.5px solid
        ${({$active, theme}) =>
            $active ? theme.colors.secondary.main : '#e6e8ec'};
    border-radius: 8px;
    padding: 4px 10px;
    background: #fff;
    color: ${({$active, theme}) =>
        $active ? theme.colors.secondary.main : '#4b5563'};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: default;
    }
`;

const ChipIcon = styled(Icon)`
    width: 14px;
    height: 14px;
`;

const PhoneDoor = styled.div`
    display: none;
    position: relative;

    ${mobile`
        display: block;
    `}
`;

const ToolsMenu = styled.div`
    position: relative;
`;

const Chevron = styled(Icon)<{$open: boolean}>`
    width: 10px;
    height: 10px;
    transform: rotate(${({$open}) => ($open ? 180 : 0)}deg);
    transition: transform 0.15s;
`;

// Drops below its button, over the viewport, its left edge under the
// button's; its right edge under the button's instead where the button sits
// near the bar's end (icons only, or a phone).
const ToolsPanel = styled.div<{$alignRight: boolean}>`
    position: absolute;
    top: calc(100% + 8px);
    ${({$alignRight}) => ($alignRight ? 'right: 0;' : 'left: 0;')}
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 100%;
    padding: 8px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

    ${mobile`
        gap: 2px;
        padding: 4px;
    `}
`;

const ToolRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 6px 8px;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;

    ${mobile`
        gap: 16px;
        padding: 4px 6px;
        font-size: 12px;
    `}
`;

const ToolName = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;

    ${mobile`
        gap: 6px;
    `}
`;

const ToolIcon = styled(Icon)`
    width: 16px;
    height: 16px;

    ${mobile`
        width: 14px;
        height: 14px;
    `}
`;

const Switch = styled.button<{$on: boolean}>`
    position: relative;
    flex-shrink: 0;
    width: 34px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 10px;
    background: ${({$on, theme}) =>
        $on ? theme.colors.primary.main : '#d1d5db'};
    cursor: pointer;
    transition: background 0.15s;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        transform: translateX(${({$on}) => ($on ? 14 : 0)}px);
        transition: transform 0.15s;
    }

    ${mobile`
        width: 28px;
        height: 16px;
        border-radius: 8px;

        &::after {
            width: 12px;
            height: 12px;
        }
    `}

    ${({$on}) =>
        $on &&
        css`
            ${mobile`
                &::after {
                    transform: translateX(12px);
                }
            `}
        `}
`;

const DoorPopover = styled.div<{$alignHide?: boolean}>`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    z-index: 5;
    display: flex;
    gap: 2px;
    padding: 4px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateX(-50%);

    /* PhoneDoor is already mobile-only, so no media query is needed here. */
    ${({$alignHide}) =>
        $alignHide &&
        css`
            left: auto;
            /* 4px menu padding + 3px switch padding, so Hide meets the door button. */
            right: -7px;
            transform: none;
        `}
`;

const LabelledButton = styled.button<{$active?: boolean}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid
        ${({$active, theme}) =>
            $active ? theme.colors.primary.main : '#e6e8ec'};
    border-radius: 10px;
    padding: 6px 10px;
    background: #fff;
    color: ${({$active, theme}) =>
        $active ? theme.colors.primary.main : '#374151'};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    ${mobile`
        padding: 5px;
        gap: 0;
    `}
`;

const ResetView = styled(LabelledButton)`
    ${mobile`
        padding: 6px;
    `}
`;

const ControlIcon = styled(Icon)`
    width: 16px;
    height: 16px;
`;

const DesktopFronts = styled.div`
    display: inline-flex;

    ${mobile`
        display: none;
    `}
`;

const FrontsSpinner = styled(Spinner)`
    width: 0.7rem;
    height: 0.7rem;
    border-width: 0.12em;
`;

const DoorFrontsLabel = styled.span.attrs({
    className: 'planner-right-label',
})`
    padding: 0 4px 0 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
    white-space: nowrap;
`;

const CompactLabel = styled.span.attrs({
    className: 'planner-compact-label',
})`
    ${narrowPlanner} {
        display: none;
    }
`;

const IconOnlyLabel = styled.span.attrs({
    className: 'planner-right-label',
})``;

export default TopBar;
